import React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useHelpTextId } from '../../hooks/useHelpTextId'
import { getInputStyles } from '../Input/arcade-input-styles'
import ArcadeHelpText, {
  ArcadeHelpTextComponentProps,
  combineAriaDescribedBy,
  getErrorIcon,
} from '../Input/ArcadeHelpText'

export type ArcadeTextAreaProps = Pick<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  | 'value'
  | 'onChange'
  | 'placeholder'
  | 'defaultValue'
  | 'disabled'
  | 'name'
  | 'id'
  | 'minLength'
  | 'maxLength'
  | 'required'
  | 'aria-label'
  | 'aria-describedby'
  | 'aria-invalid'
  | 'aria-required'
  | 'autoFocus'
  | 'readOnly'
  | 'spellCheck'
  | 'autoComplete'
> &
  ArcadeHelpTextComponentProps & {
    shouldShowErrorIcon?: boolean
  }

const TextAreaContainer = styled.div<{ isInvalid: boolean }>`
  ${({ isInvalid }) =>
    isInvalid &&
    css`
      position: relative;
    `}
`

const StyledTextArea = styled.textarea<{
  isInvalid: boolean
  disabled: boolean
}>`
  display: block;
  width: 100%;
  min-height: 10rem;
  resize: none;

  ${({ theme }) => theme.typography.input};

  ${({ theme, isInvalid, disabled }) =>
    getInputStyles({ theme, hasLeadingIcon: false, hasTrailingIcon: false, isInvalid, disabled })}
`

const ArcadeTextArea = React.forwardRef<HTMLTextAreaElement, ArcadeTextAreaProps>((props, ref) => {
  const {
    helpText,
    HelpTextIcon,
    isInvalid = false,
    shouldShowErrorIcon = true,
    errorText,
    disabled = false,
    disabledText,
    required,
    defaultValue,
    'aria-describedby': ariaDescribedBy,
    ...restProps
  } = props

  const helpTextId = useHelpTextId({ helpText, errorText, disabledText })

  return (
    <>
      <TextAreaContainer isInvalid={isInvalid}>
        <StyledTextArea
          aria-invalid={isInvalid}
          isInvalid={isInvalid}
          aria-required={required}
          aria-describedby={combineAriaDescribedBy(ariaDescribedBy, helpTextId)}
          disabled={disabled}
          defaultValue={defaultValue}
          ref={ref}
          {...restProps}
        />
        {getErrorIcon({ isInvalid, shouldShowErrorIcon, disabled, isTextArea: true })}
      </TextAreaContainer>
      <ArcadeHelpText
        helpTextId={helpTextId}
        helpText={helpText}
        HelpTextIcon={HelpTextIcon}
        isInvalid={isInvalid}
        errorText={errorText}
        disabled={disabled}
        disabledText={disabledText}
      />
    </>
  )
})

ArcadeTextArea.displayName = 'ArcadeTextArea'

export default ArcadeTextArea
